import React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Container from './Container';
import AuthModal from './AuthModal';
import { 
  generateLevel, generateHardModeLevel, generateTeamModeLevel, isValidMove, isValidHardModeMove, 
  combineColors, isSolved, updateRevealedState, hasGravityMode,
  hasTimeLimit, calculateTimeLimit, getColorsForLevel
} from './gameLogic';
import Timer from './Timer';
import { API_BASE_URL } from '../config';
import { getCrystalColors, getThemeColors } from '../constants/colors';
import LevelSelect from './LevelSelect';
import ModeInfo from './ModeInfo';
import { offlineStorage } from '../constants/offlineStorage';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import SolvePlayback from './SolvePlayback';
import { isStuck, HintManager } from './solver';
import TubeSizeSlider, { getInitialTubeScale } from './TubeSizeSlider';


// ============================================================================
// OPTIMIZED GAME STATE MANAGEMENT
// This component has been optimized to work with the new efficient server system:
// - Sends only move deltas instead of full game state
// - Receives optimized resume data without move history
// - Maintains local move history for undo functionality
// ============================================================================

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}


const DEFAULT_CONFIG = {
  containerHeight: 4,
  containerWidth: 60,
  crystalHeight: 50,
  enableAutoSolve: true
};

// Team Manager Component
const TeamManager = ({ theme, userId, isConnected, sendMessage, currentTeam, teamMembers, onTeamUpdate, pendingInvitations, onInvitationResponse }) => {
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [inviteUserId, setInviteUserId] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;
    
    setIsCreating(true);
    if (isConnected && sendMessage) {
      sendMessage({
        type: 'CREATE_TEAM',
        teamName: teamName.trim()
      });
    }
    setIsCreating(false);
    setShowCreateTeam(false);
    setTeamName('');
  };

  const handleInviteUser = () => {
    if (!inviteUserId.trim() || !currentTeam) return;
    
    if (isConnected && sendMessage) {
      sendMessage({
        type: 'INVITE_TO_TEAM',
        teamId: currentTeam.id,
        inviteUserId: inviteUserId.trim()
      });
    }
    setInviteUserId('');
  };

  const handleLeaveTeam = () => {
    if (!currentTeam) return;
    
    if (window.confirm('Are you sure you want to leave this team?')) {
      if (isConnected && sendMessage) {
        sendMessage({
          type: 'LEAVE_TEAM',
          teamId: currentTeam.id
        });
      }
    }
  };

  const handleAcceptInvitation = (teamId) => {
    if (isConnected && sendMessage) {
      sendMessage({
        type: 'ACCEPT_TEAM_INVITATION',
        teamId: teamId
      });
    }
    onInvitationResponse(teamId, 'accepted');
  };

  const handleDeclineInvitation = (teamId) => {
    if (isConnected && sendMessage) {
      sendMessage({
        type: 'DECLINE_TEAM_INVITATION',
        teamId: teamId
      });
    }
    onInvitationResponse(teamId, 'declined');
  };

  if (!currentTeam) {
    return (
      <div className={`${theme.container} p-4 rounded-lg border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Team Mode</h3>
        <p className={`${theme.subtext} mb-4`}>Create or join a team to collaborate on puzzles!</p>
        
        {/* Show pending invitations */}
        {pendingInvitations && pendingInvitations.length > 0 && (
          <div className="mb-4">
            <h4 className={`font-medium ${theme.text} mb-2`}>Team Invitations</h4>
            {pendingInvitations.map((invitation, index) => (
              <div key={index} className={`p-3 ${theme.subcontainer} rounded-lg mb-2`}>
                <p className={`${theme.text} mb-2`}>
                  <strong>{invitation.invitedBy}</strong> invited you to join <strong>"{invitation.teamName}"</strong>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptInvitation(invitation.teamId)}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleDeclineInvitation(invitation.teamId)}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!showCreateTeam ? (
          <button
            onClick={() => setShowCreateTeam(true)}
            className={`px-4 py-2 ${theme.button} rounded-lg transition-colors`}
          >
            Create Team
          </button>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Team name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input}`}
              maxLength={30}
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateTeam}
                disabled={!teamName.trim() || isCreating}
                className={`px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50`}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setShowCreateTeam(false);
                  setTeamName('');
                }}
                className={`px-4 py-2 ${theme.button} rounded-lg transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${theme.container} p-4 rounded-lg border ${theme.border}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold ${theme.text}`}>Team: {currentTeam.name}</h3>
        <button
          onClick={handleLeaveTeam}
          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
        >
          Leave Team
        </button>
      </div>
      
      <div className="mb-4">
        <h4 className={`font-medium ${theme.text} mb-2`}>Members ({teamMembers.length}/5)</h4>
        <div className="space-y-1">
          {teamMembers.map((member, index) => (
            <div key={index} className={`flex items-center justify-between py-1 px-2 ${theme.subcontainer} rounded`}>
              <span className={theme.text}>{member.userId}</span>
              {member.isLeader && (
                <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Leader</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {teamMembers.length < 5 && (
        <div className="space-y-3">
          <h4 className={`font-medium ${theme.text}`}>Invite Player</h4>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="User ID"
              value={inviteUserId}
              onChange={(e) => setInviteUserId(e.target.value)}
              className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg ${theme.input}`}
            />
            <button
              onClick={handleInviteUser}
              disabled={!inviteUserId.trim()}
              className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50`}
            >
              Invite
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Game Mode Selector Component
const GameModeSelector = ({ currentMode, onModeChange, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const modes = [
    { id: 'normal', name: 'Normal Mode', description: 'Classic puzzle solving' },
    { id: 'challenge', name: 'Challenge Mode', description: 'Advanced puzzles with special mechanics' },
    { id: 'team', name: 'Team Mode', description: 'Collaborate with other players' }
  ];
  
  const currentModeData = modes.find(mode => mode.id === currentMode) || modes[0];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2
          ${currentMode === 'challenge' 
            ? 'bg-red-600 hover:bg-red-700 text-white' 
            : `${theme.button} ${theme.text}`
          }
        `}
      >
        <span>{currentModeData.name}</span>
        <svg 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className={`
          absolute top-full mt-1 left-0 w-64 ${theme.container} 
          rounded-lg shadow-lg border ${theme.border} z-50
        `}>
          {modes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => {
                onModeChange(mode.id);
                setIsOpen(false);
              }}
              className={`
                w-full text-left px-4 py-3 hover:bg-opacity-80 transition-colors
                ${currentMode === mode.id ? 'bg-blue-100 dark:bg-blue-900' : ''}
              `}
            >
              <div className={`font-medium ${theme.text}`}>{mode.name}</div>
              <div className={`text-sm ${theme.subtext}`}>{mode.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CrystalPuzzle({ parentIsDarkMode, onThemeChange, theme, onUserChange }) {
  const [containers, setContainers] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [moveHistory, setMoveHistory] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tubesAdded, setTubesAdded] = useState(0);
  const [savingProgress, setSavingProgress] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [undoCount, setUndoCount] = useState(0);
  const [restartCount, setRestartCount] = useState(0);
  const [maxLevel, setMaxLevel] = useState(1);
  const [containerWidth, setContainerWidth] = useState(60);
  const [containerSpacing, setContainerSpacing] = useState(16);
  const [tubeScale, setTubeScale] = useState(getInitialTubeScale);
  const containerRef = useRef(null);
  const [isSavingState, setIsSavingState] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const colors = getCrystalColors(parentIsDarkMode);
  const [gameMode, setGameMode] = useState('normal'); // 'normal', 'challenge'
  const [isHardMode, setIsHardMode] = useState(false);
  const [normalModeLevel, setNormalModeLevel] = useState(1);
  const [hardModeLevel, setHardModeLevel] = useState(1);
  const [normalModeMaxLevel, setNormalModeMaxLevel] = useState(1);
  const [hardModeMaxLevel, setHardModeMaxLevel] = useState(1);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLimit, setTimeLimit] = useState(null);
  const [isResuming, setIsResuming] = useState(false);

  // Team multiplayer state
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isTeamMode, setIsTeamMode] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [teamModeLevel, setTeamModeLevel] = useState(1);
  const [teamModeMaxLevel, setTeamModeMaxLevel] = useState(1);

  // Level best time state
  const [levelBestTime, setLevelBestTime] = useState(null);

  // Solve playback state
  const [showPlayback, setShowPlayback] = useState(false);
  const [playbackData, setPlaybackData] = useState(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);

  // Hint / deadlock state
  const [highlightedMove, setHighlightedMove] = useState(null); // { from, to }
  const [hintMessage, setHintMessage] = useState(null);
  const hintManager = useRef(new HintManager()).current;

  // WebSocket connection via context
  const { sendMessage, isConnected, setUser, sessionId, suppressResumeRef, pendingMessagesRef } = useWebSocketContext();

  
  const saveGameState = useCallback(() => {
    if (isGuestMode || !userId || isTransitioning) {
      console.log('Skipping save: guest mode, no user, or transitioning');
      return;
    }
    
    // Only save if the player has made moves or added tubes
    if (moveCount === 0 && undoCount === 0 && tubesAdded === 0 && restartCount === 0) {
      console.log(`No moves made yet, skipping save (moves: ${moveCount}, undos: ${undoCount}, tubes: ${tubesAdded}, restarts: ${restartCount})`);
      return;
    }

    // Debug: Count total pieces to detect corruption
    let totalPieces = 0;
    const colorCounts = {};
    containers.forEach((tube, tubeIndex) => {
      tube.forEach((crystal, crystalIndex) => {
        totalPieces += crystal.size;
        colorCounts[crystal.color] = (colorCounts[crystal.color] || 0) + crystal.size;
      });
    });

    console.log(`SAVE DEBUG - Level ${currentLevel}:`);
    console.log(`Total pieces: ${totalPieces}`);
    console.log(`Color distribution:`, colorCounts);
    console.log(`Expected pieces for level ${currentLevel}:`, getColorsForLevel(currentLevel) * 4);
    console.log(`Containers:`, JSON.stringify(containers));
  
    console.log(`Saving game state: Level ${currentLevel}, Moves: ${moveCount}, Undos: ${undoCount}, Tubes: ${tubesAdded}, Restarts: ${restartCount}`);
    
    // Save locally first as backup
    offlineStorage.saveGameState({
      userId,
      level: currentLevel,
      containers,
      moveCount,
      undoCount,
      restartCount,
      tubesAdded,
      moveHistory,
      isHardMode
    });

    // Also save via WebSocket if connected
    if (isConnected && sendMessage) {
      sendMessage({
        type: 'SAVE_GAME_STATE',
        level: currentLevel,
        containers,
        moveCount,
        undoCount,
        restartCount,
        tubesAdded,
        moveHistory,
        isHardMode,
        mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal')
      });
    }
  }, [userId, currentLevel, containers, moveCount, undoCount, 
      restartCount, tubesAdded, moveHistory, isTransitioning, 
      isGuestMode, isHardMode, isTeamMode, isConnected, sendMessage]);

      const currentMaxLevel = useMemo(() => {
        if (isTeamMode) {
          return teamModeMaxLevel;
        }
        return isHardMode ? hardModeMaxLevel : normalModeMaxLevel;
      }, [isTeamMode, isHardMode, teamModeMaxLevel, hardModeMaxLevel, normalModeMaxLevel])

      const initializeLevel = useCallback(async (level, isRestart = false, explicitMode = null) => {
        if (isLoading) return;
        setIsLoading(true);
      
        // Determine the mode to use - explicit mode takes precedence
        const modeToUse = explicitMode || (isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal'));
        const isTeamModeLevel = modeToUse === 'team';
        const isHardModeLevel = modeToUse === 'hard';
        
        let levelContainers = [];
      
        if (isGuestMode || isRestart) {
          // Generate level locally for guests or restarts
          let newContainers;
          if (isTeamModeLevel) {
            newContainers = generateTeamModeLevel(level);
          } else if (isHardModeLevel) {
            newContainers = generateHardModeLevel(level);
          } else {
            newContainers = generateLevel(level);
          }
          setContainers(newContainers);
          setMoveHistory([]);
          setIsTimerActive(false);
          setSelectedContainer(null);
          setTubesAdded(0);
          setMoveCount(0);
          setUndoCount(0);
          if (isRestart) {
            setRestartCount(prev => prev + 1);
          }
        } else {
          try {
            let savedState = null;
            let levelTemplate = null;
            
            // Try WebSocket first if connected
            if (isConnected && sendMessage) {
              console.log(`Requesting game state via WebSocket for ${modeToUse} mode...`);
              
              // Create a promise that resolves when we get the WebSocket response
              const wsResponse = await new Promise((resolve) => {
                const handleMessage = (event) => {
                  const data = event.detail;
                  if (data.type === 'GAME_STATE_LOADED') {
                    window.removeEventListener('websocket-message', handleMessage);
                    resolve(data);
                  }
                };
                
                window.addEventListener('websocket-message', handleMessage);
                
          // Send the request with the correct mode
          sendMessage({
            type: 'LOAD_GAME_STATE',
            level: level,
            mode: modeToUse
          });
                
                // Timeout after 3 seconds
                setTimeout(() => {
                  window.removeEventListener('websocket-message', handleMessage);
                  resolve({ success: false, message: 'Timeout' });
                }, 3000);
              });
              
              // Extract level template from response
              if (wsResponse.levelTemplate) {
                levelTemplate = wsResponse.levelTemplate;
                console.log(`Received level template from server for level ${level} (${modeToUse} mode)`);
              }
              
              if (wsResponse.success && wsResponse.data) {
                savedState = {
                  containers: wsResponse.data.containers,
                  move_count: wsResponse.data.move_count,
                  undo_count: wsResponse.data.undo_count,
                  restart_count: wsResponse.data.restart_count,
                  tubes_added: wsResponse.data.tubes_added,
                  move_history: wsResponse.data.move_history
                };
                
                // If the saved state is for a different level, update the current level
                if (wsResponse.data.level && wsResponse.data.level !== level) {
                  console.log(`Saved state found for level ${wsResponse.data.level}, switching from level ${level}`);
                  setCurrentLevel(wsResponse.data.level);
                  level = wsResponse.data.level; // Update the local variable for this function
                }
                
                console.log('Game state loaded via WebSocket');
              } else {
                console.log('No saved state found via WebSocket:', wsResponse.message);
              }
            }
            
            // Fallback to local storage if WebSocket didn't work
            if (!savedState) {
              savedState = await offlineStorage.loadGameState(userId, level, isHardMode);
              if (savedState) {
                console.log('Game state loaded from local storage');
              }
            }
            
            if (savedState && savedState.containers && savedState.containers.length > 0) {
              levelContainers = savedState.containers;
              
              console.log('Applying saved state:', {
                level: level,
                containers: savedState.containers,
                moveCount: savedState.move_count,
                undoCount: savedState.undo_count,
                tubesAdded: savedState.tubes_added
              });
              
              setContainers(savedState.containers);
              setMoveCount(savedState.move_count || 0);
              setUndoCount(savedState.undo_count || 0);
              setRestartCount(savedState.restart_count || 0);
              setTubesAdded(savedState.tubes_added || 0);
              setMoveHistory(savedState.move_history || []);
              setSelectedContainer(null);
              setIsTimerActive(false);
              
              console.log(`Resumed game state for level ${level} (${isHardMode ? 'hard' : 'normal'} mode) with ${savedState.move_count} moves`);
            } else {
              // Use level template from server if available, otherwise generate locally
              if (levelTemplate && levelTemplate.containers) {
                console.log(`Using server-provided level template for level ${level} (${modeToUse} mode)`);
                console.log(`Server template containers:`, JSON.stringify(levelTemplate.containers));
                levelContainers = levelTemplate.containers;
                
                // Debug: Log what we're about to set
                console.log(`About to set containers to:`, JSON.stringify(levelTemplate.containers));
                setContainers(levelTemplate.containers);
                
                // Debug: Verify the containers were set correctly
                setTimeout(() => {
                  console.log(`Containers after setState (async check):`, JSON.stringify(containers));
                }, 100);
              } else {
                console.log(`No server template available, generating level locally for level ${level} (${modeToUse} mode)`);
                levelContainers = modeToUse === 'hard' 
                  ? generateHardModeLevel(level)
                  : (modeToUse === 'team' ? generateTeamModeLevel(level) : generateLevel(level));
                setContainers(levelContainers);
              }
              
              setMoveHistory([]);
              setSelectedContainer(null);
              setTubesAdded(0);
              setMoveCount(0);
              setUndoCount(0);
              setRestartCount(0);
              setIsTimerActive(false);
              
              console.log(`Initialized fresh level ${level} (${modeToUse} mode)`);
            }
          } catch (error) {
            console.error('Error loading game state:', error);
            // Generate fresh level on error
            levelContainers = isHardMode 
              ? generateHardModeLevel(level)
              : generateLevel(level);
            setContainers(levelContainers);
            setMoveHistory([]);
            setSelectedContainer(null);
            setTubesAdded(0);
            setMoveCount(0);
            setUndoCount(0);
            setRestartCount(0);
            setIsTimerActive(false);
          }
        }
      
        // Calculate timer using the levelContainers we just created
        if (isHardMode && hasTimeLimit(level, isHardMode) && Array.isArray(levelContainers)) {
          const uniqueColors = new Set();
          levelContainers.forEach(tube => {
            if (Array.isArray(tube)) {
              tube.forEach(crystal => {
                if (crystal && typeof crystal.color !== 'undefined') {
                  uniqueColors.add(crystal.color);
                }
              });
            }
          });
          
          const numColors = uniqueColors.size || getColorsForLevel(level);
          setTimeLimit(calculateTimeLimit(level, numColors));
        } else {
          setTimeLimit(null);
        }
        
        setIsLoading(false);
      }, [userId, isLoading, isGuestMode, isHardMode, isConnected, sendMessage]);

  


  const handleUserLoaded = useCallback(({ normalLevel, hardLevel, userId, isDarkMode }) => {
    if (userId === null) {
      // User logged out - reset everything to guest mode
      setUserId(null);
      setIsGuestMode(true);
      setCurrentLevel(1);
      setNormalModeLevel(1);
      setHardModeLevel(1);
      setNormalModeMaxLevel(1);
      setHardModeMaxLevel(1);
      setContainers([]);
      setMoveHistory([]);
      setMoveCount(0);
      setUndoCount(0);
      setRestartCount(0);
      setTubesAdded(0);
      setSelectedContainer(null);
      setIsTimerActive(false);
      setTimeLimit(null);
      setCurrentTeam(null);
      setTeamMembers([]);
      setIsTeamMode(false);
      setGameMode('normal');
      setIsHardMode(false);
      
      // Clear user from WebSocket context
      setUser(null);
      
      // Initialize guest level
      initializeLevel(1, false);
      return;
    }
    
    // Apply dark mode preference from server if provided
    if (typeof isDarkMode === 'boolean') {
      onThemeChange(isDarkMode);
    }
    
    setUserId(userId);
    setIsGuestMode(false);
    setNormalModeLevel(normalLevel || 1);
    setHardModeLevel(hardLevel || 1);
    setNormalModeMaxLevel(normalLevel || 1);
    setHardModeMaxLevel(hardLevel || 1);
    
    // Set user in WebSocket context
    setUser(userId);
    
    // Don't initialize level here - let the effect handle it after WebSocket connects
    // Just set the current level based on the active mode
    setCurrentLevel(isHardMode ? (hardLevel || 1) : (normalLevel || 1));
  }, [isHardMode, setUser, initializeLevel, onThemeChange]);

  const handleLevelSelect = useCallback(async (level) => {
    if (level <= currentMaxLevel) {
      console.log(`Level select: changing from ${currentLevel} to ${level}`);
      
      // Set current level
      setCurrentLevel(level);
      
      if (!isGuestMode && userId && isConnected && sendMessage) {
        console.log(`Requesting session for level ${level}...`);
        
        // Request specific level session via WebSocket (now fixed on server)
        const wsResponse = await new Promise((resolve) => {
          const handleMessage = (event) => {
            const data = event.detail;
            if (data.type === 'LEVEL_SESSION_LOADED') {
              window.removeEventListener('websocket-message', handleMessage);
              resolve(data);
            }
          };
          
          window.addEventListener('websocket-message', handleMessage);
          
          // Send the request for this specific level
          sendMessage({
            type: 'LOAD_LEVEL_SESSION',
            level: level,
            mode: isHardMode ? 'hard' : 'normal'
          });
          
          // Timeout after 3 seconds
          setTimeout(() => {
            window.removeEventListener('websocket-message', handleMessage);
            resolve({ success: false, message: 'Timeout' });
          }, 3000);
        });
        
        if (wsResponse.success && wsResponse.session) {
          console.log(`Found saved session for level ${level}:`, wsResponse.session);
          // The LEVEL_SESSION_LOADED handler will apply the state
          return;
        } else {
          console.log(`No saved session for level ${level}, generating fresh level`);
          // Generate fresh level if no saved session
          initializeLevel(level, false);
        }
      } else {
        // Guest mode, offline, or no WebSocket - initialize level locally
        // (initializeLevel already falls back to local storage then local generation)
        console.log(`Offline or guest mode — initializing level ${level} locally`);
        initializeLevel(level, false);
      }
    }
  }, [currentMaxLevel, currentLevel, initializeLevel, isGuestMode, userId, isConnected, sendMessage, isHardMode]);

  const handleLevelComplete = useCallback(async () => {
    const nextLevel = currentLevel + 1;
    setIsTransitioning(true);
  
    try {
      if (!isGuestMode) {
        // Try WebSocket first if connected
        if (isConnected && sendMessage) {
          const success = sendMessage({
            type: 'LEVEL_COMPLETED',
            level: nextLevel,
            tubesAdded,
            movesMade: moveCount,
            undosUsed: undoCount,
            restarts: restartCount,
            isHardMode,
            mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal')
          });
          
          if (!success) {
            // Fallback to offline storage
            const offlineSuccess = await offlineStorage.updateProgress({
              userId,
              level: nextLevel,
              tubesAdded,
              movesMade: moveCount,
              undosUsed: undoCount,
              restarts: restartCount,
              isHardMode
            });
            
            if (!offlineSuccess && navigator.onLine) {
              throw new Error('Failed to update progress');
            }
          }
        } else {
          // Use offline storage if WebSocket not available
          const success = await offlineStorage.updateProgress({
            userId,
            level: nextLevel,
            tubesAdded,
            movesMade: moveCount,
            undosUsed: undoCount,
            restarts: restartCount,
            isHardMode
          });
          
          if (!success && navigator.onLine) {
            throw new Error('Failed to update progress');
          }
        }
      }
      
      // Update the appropriate level state
      if (isHardMode) {
        setHardModeLevel(nextLevel);
        setHardModeMaxLevel(prev => Math.max(prev, nextLevel));
      } else {
        setNormalModeLevel(nextLevel);
        setNormalModeMaxLevel(prev => Math.max(prev, nextLevel));
      }
      
      setCurrentLevel(nextLevel);
      if (isGuestMode) {
        setMaxLevel(prev => Math.max(prev, nextLevel));
      }
      await initializeLevel(nextLevel, false);
    } catch (error) {
      console.error('Error during level completion:', error);
    } finally {
      setIsTransitioning(false);
    }
  }, [currentLevel, isGuestMode, userId, tubesAdded, moveCount, 
      undoCount, restartCount, isHardMode, initializeLevel, isConnected, sendMessage]);

useEffect(() => {
  const calculateContainersPerRow = () => {
    if (containerRef.current) {
      const availableWidth = containerRef.current.offsetWidth;
      const tubeWidth = containerWidth + containerSpacing;
      const maxContainers = Math.floor(availableWidth / tubeWidth);
      return maxContainers;
    }
    return 1;
  };

  const handleResize = () => {
    // Recalculate on resize
    calculateContainersPerRow();
  };

  window.addEventListener('resize', handleResize);
  handleResize();

  return () => window.removeEventListener('resize', handleResize);
}, [containerWidth, containerSpacing]);
  
useEffect(() => {
  if (!userId) {
    // Only handle guest mode in this effect
    setIsGuestMode(true);
    setCurrentLevel(1);
    setMaxLevel(1);
    initializeLevel(1, false);
  } else {
    // User is logged in, make sure guest mode is off
    setIsGuestMode(false);
  }
}, [userId, initializeLevel]);

// Effect to handle game initialization when user logs in
useEffect(() => {
  if (userId && !isGuestMode && containers.length === 0 && !isResuming) {
    if (isConnected) {
      console.log('User logged in and WebSocket connected, waiting for game resume...');
      
      // Set a timeout to initialize a fresh level if no resume response comes back
      // The WebSocketContext automatically sends REQUEST_GAME_RESUME after authentication
      const resumeTimeout = setTimeout(() => {
        if (containers.length === 0 && !isResuming) {
          console.log('No game resume response, initializing fresh level...');
          const levelToLoad = isHardMode ? hardModeLevel : normalModeLevel;
          initializeLevel(levelToLoad, false);
        }
      }, 3000); // Wait 3 seconds for resume response
      
      return () => clearTimeout(resumeTimeout);
    } else {
      // OFFLINE: User is logged in but not connected — initialize from local storage or generate fresh
      console.log('User logged in but offline — initializing level locally...');
      const levelToLoad = isHardMode ? hardModeLevel : normalModeLevel;
      initializeLevel(levelToLoad, false);
    }
  }
}, [userId, isConnected, isGuestMode, containers.length, isResuming, isHardMode, hardModeLevel, normalModeLevel, initializeLevel]);

useEffect(() => {
  if (userId && isConnected && sendMessage) {
    sendMessage({
      type: 'UPDATE_THEME_PREFERENCE',
      isDarkMode: parentIsDarkMode
    });
  }
}, [userId, parentIsDarkMode, isConnected, sendMessage]);

  // Handle WebSocket messages for game resumption and move synchronization
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      const data = event.detail;
      
      if (data.type === 'GAME_RESUME') {
        if (data.success && data.session) {
          console.log('Resuming game session:', data.session);
          console.log('Session containers:', JSON.stringify(data.session.containers));
          
          // Safety check: Don't resume a session that's below the user's known progress level
          // This prevents stale replay sessions from sending the user back to old levels
          const sessionIsHardMode = data.session.mode === 'hard';
          const sessionIsTeamMode = data.session.mode === 'team';
          
          if (!sessionIsTeamMode) {
            const knownProgressLevel = sessionIsHardMode ? hardModeLevel : normalModeLevel;
            if (data.session.level < knownProgressLevel) {
              console.log(`SAFETY: Ignoring stale GAME_RESUME — session level ${data.session.level} is below known progress level ${knownProgressLevel} (${data.session.mode} mode). Initializing fresh level instead.`);
              initializeLevel(knownProgressLevel, false);
              return;
            }
          }
          
          // SPOTTY WIFI FIX: If the client has a more advanced state than the server
          // (because some moves were made while offline and already synced via pending queue),
          // don't let the server's stale GAME_RESUME overwrite the local state.
          // Compare: if the server session is for the same level but has fewer moves,
          // and the client already has containers loaded, the client state is ahead.
          if (containers.length > 0 && data.session.level === currentLevel) {
            const serverMoveCount = data.session.moveCount || 0;
            const localMoveCount = moveCount;
            
            if (serverMoveCount < localMoveCount) {
              console.log(`SPOTTY WIFI SAFETY: Ignoring GAME_RESUME with stale state — server has ${serverMoveCount} moves but local has ${localMoveCount} moves for level ${currentLevel}. Keeping local state.`);
              return;
            }
          }
          
          setIsResuming(true);
          
          if (sessionIsTeamMode) {
            setIsTeamMode(true);
            setIsHardMode(false);
            setGameMode('team');
            
            // Don't request team status here - we're already resuming a team session
            // The team status should have been loaded when switching to team mode
          } else {
            setIsTeamMode(false);
            setIsHardMode(sessionIsHardMode);
            setGameMode(sessionIsHardMode ? 'challenge' : 'normal');
          }
          
          // Restore the complete game state
          setCurrentLevel(data.session.level);
          
          // Store level best time data if provided
          if (data.levelBestTime) {
            setLevelBestTime(data.levelBestTime);
            console.log('Level best time loaded:', data.levelBestTime.formatted, 'by', data.levelBestTime.completedBy);
          } else {
            setLevelBestTime(null);
          }
          
          // Use setTimeout to ensure state updates are applied in the next tick
          setTimeout(() => {
            setContainers(data.session.containers || []);
            setMoveHistory(data.session.moveHistory || []);
            setMoveCount(data.session.moveCount || 0);
            setUndoCount(data.session.undoCount || 0);
            setRestartCount(data.session.restartCount || 0);
            setTubesAdded(data.session.tubesAdded || 0);
            setSelectedContainer(data.session.selectedTube >= 0 ? data.session.selectedTube : null);
            
            // Handle timer state
            if (data.session.timerActive && data.session.timeRemaining) {
              setTimeLimit(data.session.timeRemaining);
              setIsTimerActive(true);
            } else {
              setIsTimerActive(false);
              if (sessionIsHardMode && hasTimeLimit(data.session.level, sessionIsHardMode)) {
                const numColors = getColorsForLevel(data.session.level);
                setTimeLimit(calculateTimeLimit(data.session.level, numColors));
              } else {
                setTimeLimit(null);
              }
            }
            
            setIsResuming(false);
            
            console.log(`Game resumed: Level ${data.session.level} (${data.session.mode} mode) with ${data.session.moveCount} moves`);
            console.log('Applied containers:', JSON.stringify(data.session.containers));
          }, 0);
        } else {
          console.log('No active session to resume:', data.message);
          // Initialize a fresh level since there's no session to resume
          // Use the requested mode to determine the correct level, not the current mode state
          let levelToLoad;
          if (data.requestedMode === 'team') {
            levelToLoad = teamModeLevel;
          } else if (data.requestedMode === 'hard') {
            levelToLoad = hardModeLevel;
          } else if (data.requestedMode === 'normal') {
            levelToLoad = normalModeLevel;
          } else {
            // Fallback to current mode if no requested mode specified
            levelToLoad = isTeamMode ? teamModeLevel : (isHardMode ? hardModeLevel : normalModeLevel);
          }
          console.log(`No session found for ${data.requestedMode || 'current'} mode, initializing level ${levelToLoad}`);
          initializeLevel(levelToLoad, false);
        }
      }
    
    if (data.type === 'RECEIVE_MOVE') {
      const { moveData, fromSessionId, fromUserId } = data;
      
      // Handle both individual session moves and team moves
      const isTeamMove = fromUserId && isTeamMode;
      const modeMatch = isTeamMove ? 
        (moveData.mode === 'team') : 
        (moveData.mode === (isHardMode ? 'hard' : 'normal'));
      
      // Only apply the move if it's for the same level and mode we're currently on
      if (moveData.level === currentLevel && modeMatch) {
        
        console.log(`Received move from session ${fromSessionId}: container ${moveData.fromIdx} → ${moveData.toIdx}`);
        
        // Apply the complete move state from the other session, including move history
        setContainers(moveData.containers);
        setMoveCount(moveData.moveCount);
        setMoveHistory(moveData.moveHistory || []);
        setSelectedContainer(null); // Clear any selection
        
        // Also sync other state that might have changed
        if (moveData.tubesAdded !== undefined) {
          setTubesAdded(moveData.tubesAdded);
        }
        if (moveData.undoCount !== undefined) {
          setUndoCount(moveData.undoCount);
        }
        if (moveData.restartCount !== undefined) {
          setRestartCount(moveData.restartCount);
        }
        
        // Show a brief visual indicator that a move was received
        console.log(`Game synchronized: Level ${moveData.level} now has ${moveData.moveCount} moves, ${moveData.moveHistory?.length || 0} in history`);
        
        // Check if the puzzle is solved after the received move
        if (isSolved(moveData.containers, currentLevel, isHardMode)) {
          setTimeout(handleLevelComplete, 300);
        }
      } else {
        console.log(`Ignoring move from different level/mode: ${moveData.level} (${moveData.mode}) vs current ${currentLevel} (${isHardMode ? 'hard' : 'normal'})`);
      }
    }
    
    if (data.type === 'RECEIVE_ACTION') {
      const { actionData, fromSessionId, fromUserId } = data;
      
      // Handle both individual session actions and team actions
      const isTeamAction = fromUserId && isTeamMode;
      const isIndividualAction = fromSessionId && !isTeamMode;
      
      // Determine if this action is for the current mode
      let modeMatch = false;
      if (isTeamAction) {
        modeMatch = actionData.mode === 'team';
      } else if (isIndividualAction) {
        modeMatch = actionData.mode === (isHardMode ? 'hard' : 'normal');
      }
      
      // Only apply the action if it's for the same level and mode we're currently on
      if (actionData.level === currentLevel && modeMatch) {
        
        const actionSource = isTeamAction ? `team member ${fromUserId}` : `session ${fromSessionId}`;
        console.log(`Received ${actionData.action} action from ${actionSource}`);
        
        // Apply the action state from the other session/team member
        setContainers(actionData.containers);
        setMoveCount(actionData.moveCount);
        setUndoCount(actionData.undoCount);
        setRestartCount(actionData.restartCount);
        setTubesAdded(actionData.tubesAdded);
        setMoveHistory(actionData.moveHistory || []);
        setSelectedContainer(null); // Clear any selection
        
        // Handle specific action types
        if (actionData.action === 'restart') {
          setIsTimerActive(false);
          console.log(`Game restarted by ${actionSource}`);
        } else if (actionData.action === 'undo') {
          console.log(`Move undone by ${actionSource}: Level ${actionData.level} now has ${actionData.moveCount} moves, ${actionData.moveHistory?.length || 0} in history`);
        } else if (actionData.action === 'add_tube') {
          console.log(`Tube added by ${actionSource}: ${actionData.tubesAdded} total tubes`);
        }
        
        console.log(`Game synchronized: ${actionData.action} applied from ${actionSource}`);
        
        // Check if the puzzle is solved after the received action
        if (isSolved(actionData.containers, currentLevel, isHardMode)) {
          setTimeout(handleLevelComplete, 300);
        }
      } else {
        const currentMode = isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal');
        console.log(`Ignoring ${actionData.action} from different level/mode: ${actionData.level} (${actionData.mode}) vs current ${currentLevel} (${currentMode})`);
      }
    }
    
    if (data.type === 'LEVEL_SESSION_LOADED') {
      if (data.success && data.session) {
        console.log('Level session loaded:', data.session);
        
        // Store level best time data if provided
        if (data.levelBestTime) {
          setLevelBestTime(data.levelBestTime);
          console.log('Level best time loaded:', data.levelBestTime.formatted, 'by', data.levelBestTime.completedBy);
        } else {
          setLevelBestTime(null);
        }
        
        // Restore the complete game state for this level
        setContainers(data.session.containers || []);
        setMoveHistory(data.session.moveHistory || []);
        setMoveCount(data.session.moveCount || 0);
        setUndoCount(data.session.undoCount || 0);
        setRestartCount(data.session.restartCount || 0);
        setTubesAdded(data.session.tubesAdded || 0);
        setSelectedContainer(data.session.selectedTube >= 0 ? data.session.selectedTube : null);
        
        // Handle timer state for hard mode
        if (data.session.timerActive && data.session.timeRemaining) {
          setTimeLimit(data.session.timeRemaining);
          setIsTimerActive(true);
        } else {
          setIsTimerActive(false);
          if (isHardMode && hasTimeLimit(data.session.level, isHardMode)) {
            const numColors = getColorsForLevel(data.session.level);
            setTimeLimit(calculateTimeLimit(data.session.level, numColors));
          } else {
            setTimeLimit(null);
          }
        }
        
        console.log(`Level ${data.session.level} session loaded with ${data.session.moveCount} moves`);
      } else {
        console.log('No saved session found for level, will generate fresh level');
      }
    }

    if (data.type === 'GAME_STATE_LOADED') {
      // Always check for level best time data, regardless of success status
      if (data.levelBestTime) {
        setLevelBestTime(data.levelBestTime);
        console.log('Level best time loaded via GAME_STATE_LOADED:', data.levelBestTime.formatted, 'by', data.levelBestTime.completedBy);
      } else {
        setLevelBestTime(null);
      }
      
      // Handle saved state data only if successful
      if (data.success && data.data) {
        // Additional saved state handling could go here if needed
      }
    }

    // Team management message handlers
    if (data.type === 'TEAM_CREATED') {
      if (data.success) {
        console.log('Team created successfully:', data.team);
        setCurrentTeam(data.team);
        setTeamMembers(data.members);
      } else {
        console.error('Failed to create team:', data.error);
        alert(`Failed to create team: ${data.error}`);
      }
    }

    if (data.type === 'TEAM_UPDATED') {
      console.log('Team updated:', data.members);
      setTeamMembers(data.members);
    }

    if (data.type === 'TEAM_LEFT') {
      if (data.success) {
        console.log('Left team successfully');
        setCurrentTeam(null);
        setTeamMembers([]);
      } else {
        console.error('Failed to leave team:', data.error);
        alert(`Failed to leave team: ${data.error}`);
      }
    }

    if (data.type === 'INVITE_SENT') {
      if (data.success) {
        console.log('User invited successfully');
        if (data.members) {
          setTeamMembers(data.members);
        }
      } else {
        console.error('Failed to invite user:', data.error);
        alert(`Failed to invite user: ${data.error}`);
      }
    }

    if (data.type === 'TEAM_INVITATION') {
      console.log('Received team invitation:', data);
      setPendingInvitations(prev => [...prev, {
        teamId: data.teamId,
        teamName: data.teamName,
        invitedBy: data.invitedBy,
        message: data.message
      }]);
    }

    if (data.type === 'TEAM_INVITATION_RESPONSE') {
      if (data.success) {
        console.log('Successfully joined team:', data.team);
        setCurrentTeam(data.team);
        setTeamMembers(data.members);
        // Clear pending invitations
        setPendingInvitations([]);
      } else {
        console.error('Failed to join team:', data.error);
        alert(`Failed to join team: ${data.error}`);
      }
    }

    if (data.type === 'TEAM_INVITATION_DECLINED') {
      console.log('Team invitation declined');
    }

    if (data.type === 'TEAM_STATUS') {
      if (data.success) {
        console.log('Team status loaded:', data);
        if (data.currentTeam) {
          setCurrentTeam(data.currentTeam);
          setTeamMembers(data.teamMembers);
          // Set team-specific levels from team progress - use highest level as max level
          setTeamModeLevel(data.currentTeam.highestLevel || 1);
          setTeamModeMaxLevel(data.currentTeam.highestLevel || 1);
          
          // If we're in team mode, update current level to team's highest level and try to resume game
          if (isTeamMode) {
            const teamLevel = data.currentTeam.highestLevel || 1;
            setCurrentLevel(teamLevel);
            
            // Now that we know the user is in a team, request game resume for team mode
            if (isConnected && sendMessage) {
              console.log(`User is in team, requesting team game resume at level ${teamLevel}`);
              sendMessage({
                type: 'REQUEST_GAME_RESUME',
                preferTeamMode: true,
                requestedMode: 'team'
              });
            } else {
              // Fallback to initializing fresh level if not connected
              initializeLevel(teamLevel, false, 'team');
            }
          }
        } else {
          // User not in a team - they can't play team mode
          console.log('User not in a team, cannot access team mode');
          setCurrentTeam(null);
          setTeamMembers([]);
          setTeamModeLevel(1);
          setTeamModeMaxLevel(1);
          
          // If they're trying to access team mode but not in a team, show level 1 but don't allow gameplay
          if (isTeamMode) {
            setCurrentLevel(1);
            setContainers([]); // Clear containers to prevent gameplay
          }
        }
        if (data.pendingInvitations) {
          setPendingInvitations(data.pendingInvitations);
        }
      } else {
        console.error('Failed to load team status:', data.error);
      }
    }

    // Handle server-side undo response
    if (data.type === 'UNDO_RESULT') {
      if (data.success) {
        console.log('Undo successful, applying server-reconstructed state');
        
        // OPTIMIZED: Server has already reconstructed the previous state
        // Just apply the containers and state that the server sends back
        setContainers(data.containers);
        setMoveCount(data.moveCount);
        setUndoCount(data.undoCount);
        setTubesAdded(data.tubesAdded);
        setRestartCount(data.restartCount);
        setSelectedContainer(null);
        
        console.log(`Optimized undo applied: Level ${currentLevel} now has ${data.moveCount} moves`);
        
        // Log the move details for debugging
        if (data.moveToUndo) {
          const { fromTube, toTube, crystalColor, crystalSize } = data.moveToUndo;
          console.log(`Undid move: ${fromTube} → ${toTube}, crystal: color=${crystalColor}, size=${crystalSize}`);
        }
      } else {
        console.error('Undo failed:', data.error);
        // Optionally show user feedback
        if (data.error === 'No moves to undo') {
          console.log('No moves available to undo');
        }
      }
    }
  };

  window.addEventListener('websocket-message', handleWebSocketMessage);
  
  return () => {
    window.removeEventListener('websocket-message', handleWebSocketMessage);
  };
}, [isHardMode, hardModeLevel, normalModeLevel, initializeLevel, currentLevel, handleLevelComplete]);

const makeMove = useCallback((fromIdx, toIdx) => {
  const validationFunction = isHardMode ? isValidHardModeMove : isValidMove;
  if (!validationFunction(containers, fromIdx, toIdx, currentLevel, isHardMode)) return;
  
  // Only start timer on first move (when moveCount is 0)
  if (timeLimit && !isTimerActive && isHardMode && moveCount === 0) {
    setIsTimerActive(true);
  }
  
  const newMoveCount = moveCount + 1;
  setMoveCount(newMoveCount);
  
  const previousState = containers.map(container => [...container]);
  
  // Create the new move history entry
  const newMoveEntry = { 
    containers: previousState, 
    from: fromIdx, 
    to: toIdx,
    tubesAdded
  };
  
  // Update move history with the new entry
  const newMoveHistory = [...moveHistory, newMoveEntry];
  setMoveHistory(newMoveHistory);

  const newContainers = [...containers];
  const movingCrystal = newContainers[fromIdx].pop();
  
  // Check if gravity mode is active
  if (isHardMode && hasGravityMode(currentLevel, isHardMode)) {
      // Insert at the beginning (bottom) of the tube
      newContainers[toIdx] = [movingCrystal, ...newContainers[toIdx]];
  } else {
      // Normal mode - add to top
      newContainers[toIdx] = [...newContainers[toIdx], movingCrystal];
  }

  // First combine colors
  const combinedContainers = combineColors(newContainers);
  // Then update revealed state
  const updatedContainers = updateRevealedState(combinedContainers, currentLevel, isHardMode);
  
  setContainers(updatedContainers);
  setSelectedContainer(null);

    // OPTIMIZED: Create move data for server
    const lastMove = {
      from: fromIdx,
      to: toIdx,
      crystalColor: movingCrystal.color,
      crystalSize: movingCrystal.size,
      tubesAddedBefore: tubesAdded
    };

    console.log(`DEBUG: Creating lastMove with crystalColor=${movingCrystal.color}, crystalSize=${movingCrystal.size}`);

    // Save game state with the correct move count and optimized move data
    if (!isGuestMode && userId && !isTransitioning) {
      // Save locally first as backup (keep full history for local undo)
      offlineStorage.saveGameState({
        userId,
        level: currentLevel,
        containers: updatedContainers,
        moveCount: newMoveCount,
        undoCount,
        restartCount,
        tubesAdded,
        moveHistory: newMoveHistory,
        isHardMode
      });

      // OPTIMIZED: Save via WebSocket with only last move
      if (isConnected && sendMessage) {
        sendMessage({
          type: 'SAVE_GAME_STATE',
          level: currentLevel,
          containers: updatedContainers,
          moveCount: newMoveCount,
          undoCount,
          restartCount,
          tubesAdded,
          lastMove, // OPTIMIZED: Send only the last move, not full history
          isHardMode,
          mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal')
        });

        // OPTIMIZED: Broadcast move to other sessions (send only move delta)
        if (isTeamMode && currentTeam) {
          // Broadcast to team members
          sendMessage({
            type: 'BROADCAST_TEAM_MOVE',
            teamId: currentTeam.id,
            level: currentLevel,
            mode: 'team',
            fromIdx,
            toIdx,
            containers: updatedContainers,
            moveCount: newMoveCount,
            tubesAdded,
            undoCount,
            restartCount
          });
        } else {
          // Broadcast to other sessions of the same user
          sendMessage({
            type: 'BROADCAST_MOVE',
            level: currentLevel,
            mode: isHardMode ? 'hard' : 'normal',
            fromIdx,
            toIdx,
            containers: updatedContainers,
            moveCount: newMoveCount,
            tubesAdded,
            undoCount,
            restartCount
          });
        }
      }
    }

  if (isSolved(updatedContainers, currentLevel, isHardMode)) {
      setTimeout(handleLevelComplete, 300);
  }
}, [containers, handleLevelComplete, tubesAdded, currentLevel, 
  isHardMode, isTeamMode, currentTeam, timeLimit, isTimerActive, moveCount, isGuestMode, userId, 
  isTransitioning, undoCount, restartCount, moveHistory, isConnected, sendMessage]);



  const handleContainerClick = useCallback((index) => {
  if (selectedContainer === null) {
    if (containers[index]?.length > 0) {
      setSelectedContainer(index);
    }
  } else {
    if (selectedContainer !== index) {
      makeMove(selectedContainer, index);
    } else {
      setSelectedContainer(null);
    }
  }
}, [selectedContainer, containers, makeMove, timeLimit, isTimerActive]);

  const handleCheat = useCallback(() => {
    const newContainers = [...containers, []];
    // Check if the puzzle is solvable with the new tube
    if (isSolved(newContainers)) {
      return; // Don't add the tube if it would make the puzzle immediately solved
    }
    
    const newTubesAdded = tubesAdded + 1;
    setContainers(newContainers);
    setTubesAdded(newTubesAdded);

    // Save locally and broadcast add tube action
    if (!isGuestMode && userId && !isTransitioning) {
      // Always save locally first
      offlineStorage.saveGameState({
        userId,
        level: currentLevel,
        containers: newContainers,
        moveCount,
        undoCount,
        restartCount,
        tubesAdded: newTubesAdded,
        moveHistory,
        isHardMode
      });

      // Broadcast to other sessions or team members if connected
      if (isConnected && sendMessage) {
        if (isTeamMode && currentTeam) {
          sendMessage({
            type: 'BROADCAST_TEAM_ACTION',
            teamId: currentTeam.id,
            action: 'add_tube',
            level: currentLevel,
            mode: 'team',
            containers: newContainers,
            moveCount,
            undoCount,
            restartCount,
            tubesAdded: newTubesAdded,
            moveHistory
          });
        } else {
          sendMessage({
            type: 'BROADCAST_ACTION',
            action: 'add_tube',
            level: currentLevel,
            mode: isHardMode ? 'hard' : 'normal',
            containers: newContainers,
            moveCount,
            undoCount,
            restartCount,
            tubesAdded: newTubesAdded,
            moveHistory
          });
        }
      }
    }
  }, [containers, tubesAdded, moveCount, undoCount, restartCount, moveHistory, 
      currentLevel, isHardMode, isGuestMode, userId, isTransitioning, isConnected, sendMessage]);

  // OPTIMIZED: Server-side undo functionality
  const handleUndo = useCallback(() => {
    // For guest mode, handle undo locally using moveHistory
    if (isGuestMode) {
      if (moveHistory.length === 0) {
        console.log('No moves to undo (guest mode)');
        return;
      }
      
      const newUndoCount = undoCount + 1;
      const newMoveCount = moveCount - 1;
      const lastMove = moveHistory[moveHistory.length - 1];
      const newMoveHistory = moveHistory.slice(0, -1);
      
      setUndoCount(newUndoCount);
      setMoveCount(newMoveCount);
      setContainers(lastMove.containers);
      setTubesAdded(lastMove.tubesAdded || 0);
      setMoveHistory(newMoveHistory);
      setSelectedContainer(null);
      return;
    }

    // OPTIMIZED: For connected users, check moveCount instead of moveHistory
    if (moveCount === 0) {
      console.log('No moves to undo (moveCount is 0)');
      return;
    }

    // For connected users, send undo request to server
    if (isConnected && sendMessage) {
      console.log(`Sending undo request to server... (current moveCount: ${moveCount})`);
      sendMessage({
        type: 'UNDO_MOVE',
        level: currentLevel,
        mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal')
      });
    } else {
      // OFFLINE UNDO FALLBACK: If not connected, use local moveHistory if available
      if (moveHistory.length > 0) {
        console.log('Not connected — performing local undo using moveHistory');
        const newUndoCount = undoCount + 1;
        const newMoveCount = moveCount - 1;
        const lastMove = moveHistory[moveHistory.length - 1];
        const newMoveHistory = moveHistory.slice(0, -1);
        
        setUndoCount(newUndoCount);
        setMoveCount(newMoveCount);
        setContainers(lastMove.containers);
        setTubesAdded(lastMove.tubesAdded || 0);
        setMoveHistory(newMoveHistory);
        setSelectedContainer(null);
        
        // Save the undone state locally so it persists and syncs correctly on reconnect
        if (!isGuestMode && userId) {
          offlineStorage.saveGameState({
            userId,
            level: currentLevel,
            containers: lastMove.containers,
            moveCount: newMoveCount,
            undoCount: newUndoCount,
            restartCount,
            tubesAdded: lastMove.tubesAdded || 0,
            moveHistory: newMoveHistory,
            isHardMode
          });
          
          // Queue a SAVE_GAME_STATE so the server gets the undone state on reconnect
          sendMessage({
            type: 'SAVE_GAME_STATE',
            level: currentLevel,
            containers: lastMove.containers,
            moveCount: newMoveCount,
            undoCount: newUndoCount,
            restartCount,
            tubesAdded: lastMove.tubesAdded || 0,
            isHardMode,
            mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal')
          });
        }
      } else {
        console.log('Not connected to server and no local move history, cannot undo');
      }
    }
  }, [moveCount, moveHistory, isGuestMode, undoCount, isConnected, sendMessage, 
      currentLevel, isTeamMode, isHardMode, containers]);

  const scaledConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    scale: tubeScale
  }), [tubeScale]);

  const memoizedContainers = useMemo(() => 
    containers.map((container, index) => (
      <Container
        key={index}
        containerIndex={index}
        crystals={container}
        selected={selectedContainer === index}
        onClick={handleContainerClick}
        config={scaledConfig}
        colors={colors}
        theme={theme}
        isValidTarget={selectedContainer !== null ? 
          (idx) => isValidMove(containers, selectedContainer, idx, currentLevel, isHardMode) : 
          null
        }
        selectedContainerIndex={selectedContainer}
        level={currentLevel}
        isHardMode={isHardMode}
        hintHighlight={
          highlightedMove
            ? (index === highlightedMove.from ? 'from' : index === highlightedMove.to ? 'to' : null)
            : null
        }
      />
    )),
    [containers, selectedContainer, handleContainerClick, colors, theme, highlightedMove, scaledConfig]
  );

  const handleRestart = useCallback(() => {
    setIsTimerActive(false); // Reset timer active state
    const newRestartCount = restartCount + 1;
    
    // Generate fresh level containers
    const newContainers = isHardMode 
      ? generateHardModeLevel(currentLevel)
      : generateLevel(currentLevel);
    
    // Reset all game state
    setContainers(newContainers);
    setMoveHistory([]);
    setSelectedContainer(null);
    setTubesAdded(0);
    setMoveCount(0);
    setUndoCount(0);
    setRestartCount(newRestartCount);

    // Save locally and broadcast restart action
    if (!isGuestMode && userId && !isTransitioning) {
      // Always save locally first
      offlineStorage.saveGameState({
        userId,
        level: currentLevel,
        containers: newContainers,
        moveCount: 0,
        undoCount: 0,
        restartCount: newRestartCount,
        tubesAdded: 0,
        moveHistory: [],
        isHardMode
      });

      // Broadcast to other sessions or team members if connected
      if (isConnected && sendMessage) {
        if (isTeamMode && currentTeam) {
          sendMessage({
            type: 'BROADCAST_TEAM_ACTION',
            teamId: currentTeam.id,
            action: 'restart',
            level: currentLevel,
            mode: 'team',
            containers: newContainers,
            moveCount: 0,
            undoCount: 0,
            restartCount: newRestartCount,
            tubesAdded: 0,
            moveHistory: []
          });
        } else {
          sendMessage({
            type: 'BROADCAST_ACTION',
            action: 'restart',
            level: currentLevel,
            mode: isHardMode ? 'hard' : 'normal',
            containers: newContainers,
            moveCount: 0,
            undoCount: 0,
            restartCount: newRestartCount,
            tubesAdded: 0,
            moveHistory: []
          });
        }
      }
    }
  }, [currentLevel, restartCount, isHardMode, isGuestMode, userId, 
      isTransitioning, isConnected, sendMessage]);

  // Game mode change handler
  const handleGameModeChange = useCallback((newMode) => {
    setGameMode(newMode);
    
    // Handle team mode
    if (newMode === 'team') {
      setIsTeamMode(true);
      setIsHardMode(false); // Team mode uses normal difficulty
      setShowTeamManager(true);
      
      // Set level to team mode level (will be updated when team status loads)
      setCurrentLevel(teamModeLevel);
      
      // Request team status first to check if user is in a team
      if (!isGuestMode && userId && isConnected && sendMessage) {
        sendMessage({
          type: 'REQUEST_TEAM_STATUS'
        });
        
        // Only proceed with game resume if user is already in a team
        // This will be handled in the TEAM_STATUS response
      } else {
        // Guest mode - can't access team mode
        console.log('Guest mode cannot access team mode');
        setCurrentLevel(1);
      }
    } else {
      setIsTeamMode(false);
      setShowTeamManager(false);
      
      // Update isHardMode based on the selected mode
      const newIsHardMode = newMode === 'challenge';
      setIsHardMode(newIsHardMode);
      
      // Determine the mode string for server request
      const requestedMode = newIsHardMode ? 'hard' : 'normal';
      
      // IMPORTANT: Set the correct level for the mode BEFORE making requests
      const correctLevel = newIsHardMode ? hardModeLevel : normalModeLevel;
      setCurrentLevel(correctLevel);
      
      console.log(`Switching to ${requestedMode} mode: setting level to ${correctLevel} (normal: ${normalModeLevel}, hard: ${hardModeLevel})`);
      
      // Request game resume for the specific mode at the correct level
      if (!isGuestMode && userId && isConnected && sendMessage) {
        console.log(`Requesting game resume for ${requestedMode} mode at level ${correctLevel}`);
        sendMessage({
          type: 'REQUEST_GAME_RESUME',
          requestedMode: requestedMode
        });
      } else {
        // Fallback to initializing fresh level if not connected
        console.log(`Initializing fresh ${requestedMode} level ${correctLevel}`);
        initializeLevel(correctLevel, false, requestedMode);
      }
    }
  }, [hardModeLevel, normalModeLevel, teamModeLevel, initializeLevel, isGuestMode, userId, isConnected, sendMessage]);

  // mode toggle handler (kept for backward compatibility)
  const handleModeToggle = useCallback(() => {
    if (moveCount > 0) {
      if (!window.confirm('Changing modes will reset your current progress. Continue?')) {
        return;
      }
    }
    setIsHardMode(prev => {
      const newMode = !prev;
      // Update current level based on the new mode
      setCurrentLevel(newMode ? hardModeLevel : normalModeLevel);
      return newMode;
    });
  }, [moveCount, hardModeLevel, normalModeLevel]);
  
  // Removed problematic effect that was incorrectly incrementing restart count
  // Mode changes are now handled properly in the handleGameModeChange function

  // Handle solve playback request
  const handleViewSolve = useCallback(async () => {
    if (isGuestMode || !isConnected || !sendMessage) {
      console.log('Cannot view solve: guest mode or not connected');
      return;
    }

    setLoadingPlayback(true);
    
    try {
      // Request solve playback data via WebSocket
      const wsResponse = await new Promise((resolve) => {
        const handleMessage = (event) => {
          const data = event.detail;
          if (data.type === 'SOLVE_PLAYBACK_RESPONSE') {
            window.removeEventListener('websocket-message', handleMessage);
            resolve(data);
          }
        };
        
        window.addEventListener('websocket-message', handleMessage);
        
        // Send the request
        sendMessage({
          type: 'REQUEST_SOLVE_PLAYBACK',
          level: currentLevel,
          mode: isTeamMode ? 'team' : (isHardMode ? 'hard' : 'normal'),
          sessionType: isTeamMode ? 'team' : 'single'
        });
        
        // Timeout after 10 seconds
        setTimeout(() => {
          window.removeEventListener('websocket-message', handleMessage);
          resolve({ success: false, error: 'Request timeout' });
        }, 10000);
      });
      
      if (wsResponse.success && wsResponse.data) {
        console.log('Solve playback data received:', wsResponse.data);
        setPlaybackData(wsResponse.data);
        setShowPlayback(true);
      } else {
        console.error('Failed to load solve playback:', wsResponse.error);
        alert(`Failed to load solve playback: ${wsResponse.error || 'No data available'}`);
      }
    } catch (error) {
      console.error('Error requesting solve playback:', error);
      alert('Failed to load solve playback. Please try again.');
    } finally {
      setLoadingPlayback(false);
    }
  }, [currentLevel, isTeamMode, isHardMode, isGuestMode, isConnected, sendMessage]);

  // Handle closing playback
  const handleClosePlayback = useCallback(() => {
    setShowPlayback(false);
    setPlaybackData(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Deadlock detection — recheck whenever the board changes
  // ---------------------------------------------------------------------------
  const stuck = useMemo(
    () => containers.length > 0 && isStuck(containers, currentLevel, isHardMode),
    [containers, currentLevel, isHardMode]
  );

  // ---------------------------------------------------------------------------
  // Hint handler
  // ---------------------------------------------------------------------------
  const handleHint = useCallback(() => {
    setHintMessage(null);
    const hint = hintManager.getHint(containers, currentLevel, isHardMode);
    if (hint.status === 'solved' || hint.status === 'timeout') {
      setHighlightedMove(hint.move); // { from, to }
      setHintMessage(null);
    } else if (hint.status === 'unsolvable') {
      setHighlightedMove(null);
      setHintMessage('No solution found — try undoing moves or restarting.');
    } else if (hint.status === 'stuck') {
      setHighlightedMove(null);
      setHintMessage('No moves available — undo or restart to continue.');
    } else if (hint.status === 'already_solved') {
      setHighlightedMove(null);
      setHintMessage('The puzzle is already solved!');
    }
  }, [containers, currentLevel, isHardMode, hintManager]);

  // Clear hint highlight and message when the board changes (player moved / undid / restarted)
  useEffect(() => {
    setHighlightedMove(null);
    setHintMessage(null);
  }, [containers]);

  // Invalidate hint cache on restart / undo / level change / Add Tube
  useEffect(() => {
    hintManager.invalidate();
  }, [currentLevel, isHardMode, hintManager]);

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <AuthModal 
        onUserLoaded={handleUserLoaded}
        isGuestMode={isGuestMode}
        onExitGuestMode={() => setIsGuestMode(false)}
        onUserChange={onUserChange}
        theme={theme}
      />
      
      {isGuestMode && (
        <div className={`mb-4 text-center ${theme.container} p-2 rounded-lg text-sm ${theme.text}`}>
          Playing as guest - Progress won't be saved. 
        </div>
      )}

      {isTeamMode && !isGuestMode && (
        <div className="mb-4">
          <TeamManager
            theme={theme}
            userId={userId}
            isConnected={isConnected}
            sendMessage={sendMessage}
            currentTeam={currentTeam}
            teamMembers={teamMembers}
            pendingInvitations={pendingInvitations}
            onTeamUpdate={(team, members) => {
              setCurrentTeam(team);
              setTeamMembers(members);
            }}
            onInvitationResponse={(teamId, response) => {
              // Remove the invitation from pending list
              setPendingInvitations(prev => prev.filter(inv => inv.teamId !== teamId));
            }}
          />
        </div>
      )}

    <LevelSelect 
      currentLevel={currentLevel}
      maxLevel={currentMaxLevel}
      isHardMode={isHardMode}
      onLevelSelect={handleLevelSelect}
      isGuestMode={isGuestMode}
      theme={theme}
    />

    <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
      <div className="flex items-center">
        <GameModeSelector 
          currentMode={gameMode}
          onModeChange={handleGameModeChange}
          theme={theme}
        />
        <div className="ml-4">
          <ModeInfo 
            isHardMode={isHardMode}
            level={currentLevel}
            theme={theme}
          />
        </div>
      </div>
      <TubeSizeSlider
        scale={tubeScale}
        onScaleChange={setTubeScale}
        theme={theme}
      />
    </div>
      
    <div className="flex justify-between items-center mb-8">
      <div className="flex items-center gap-4">
        <div className={`text-3xl font-bold ${theme.text}`}>
          Level {currentLevel}
          {tubesAdded > 0 && (
            <span className={`ml-2 text-sm ${theme.subtext}`}>
              Extra tubes: {tubesAdded}
            </span>
          )}
          {savingProgress && (
            <span className={`ml-2 text-sm ${theme.subtext}`}>
              Saving progress...
            </span>
          )}
        </div>
        {timeLimit && (
          <Timer
            timeLimit={timeLimit}
            isActive={isTimerActive}
            onTimeExpired={() => {
              setIsTimerActive(false);
              handleRestart();
            }}
            theme={theme}
          />
        )}
      </div>
        <div className="space-x-4">
          <button 
            className={`px-6 py-2 ${theme.button} rounded-lg transition-colors`}
            onClick={handleRestart}
          >
            Restart
          </button>
          <button 
            className={`px-6 py-2 ${theme.button} rounded-lg transition-colors`}
            onClick={handleUndo}
            disabled={isGuestMode ? (moveHistory?.length === 0) : (moveCount === 0)}
          >
            Undo
          </button>
          {DEFAULT_CONFIG.enableAutoSolve && (
            <button 
              className={`px-6 py-2 ${theme.button} rounded-lg transition-colors`}
              onClick={handleCheat}
            >
              Add Tube
            </button>
          )}
          <button 
            className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-medium"
            onClick={handleHint}
          >
            💡 Hint
          </button>
        </div>
      </div>

      {/* Deadlock banner */}
      {stuck && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded-lg text-center">
          <span className="text-red-800 dark:text-red-200 font-medium">
            No useful moves left — try undoing or restarting.
          </span>
        </div>
      )}

      {/* Hint message banner */}
      {hintMessage && !stuck && (
        <div className="mb-4 p-3 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 rounded-lg text-center">
          <span className="text-amber-800 dark:text-amber-200 font-medium">
            💡 {hintMessage}
          </span>
        </div>
      )}

      {/* Game Area - Show message if in team mode but not in a team */}
      {isTeamMode && !currentTeam && !isGuestMode ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className={`${theme.container} p-8 rounded-lg border ${theme.border} text-center max-w-md`}>
            <div className={`text-6xl mb-4`}>👥</div>
            <h3 className={`text-xl font-semibold ${theme.text} mb-4`}>Join a Team to Play</h3>
            <p className={`${theme.subtext} mb-6`}>
              Team mode requires you to be part of a team. Create a team or accept an invitation to start playing collaborative puzzles.
            </p>
            <p className={`text-sm ${theme.subtext}`}>
              Use the team manager above to create a team or manage invitations.
            </p>
          </div>
        </div>
      ) : (
        <div 
          ref={containerRef} 
          className="flex flex-wrap justify-center items-start gap-0"
        >
          {memoizedContainers}
        </div>
      )}

      {/* Best Time Display */}
      {levelBestTime && !isGuestMode && (
        <div className="mt-6 text-center">
          <div className={`${theme.container} px-4 py-3 rounded-lg border ${theme.border} inline-block`}>
            <div className={`text-sm ${theme.subtext} mb-1`}>
              Best Completion Time
            </div>
            <div className={`${theme.text} font-medium mb-3`}>
              Completed {new Date(levelBestTime.completedAt).toLocaleDateString()} by{' '}
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                {levelBestTime.completedBy}
              </span>{' '}
              in{' '}
              <span className="font-bold text-green-600 dark:text-green-400">
                {(() => {
                  const ms = levelBestTime.milliseconds;
                  const totalSeconds = Math.floor(ms / 1000);
                  const milliseconds = ms % 1000;
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const seconds = totalSeconds % 60;
                  
                  let timeStr = '';
                  if (hours > 0) timeStr += `${hours}h `;
                  if (minutes > 0) timeStr += `${minutes}m `;
                  if (seconds > 0) timeStr += `${seconds}s `;
                  if (milliseconds > 0 || timeStr === '') timeStr += `${milliseconds}ms`;
                  
                  return timeStr.trim();
                })()}
              </span>
            </div>
            <button
              onClick={handleViewSolve}
              disabled={loadingPlayback}
              className={`
                px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                transition-colors text-sm font-medium disabled:opacity-50 
                disabled:cursor-not-allowed flex items-center gap-2 mx-auto
              `}
            >
              {loadingPlayback ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Loading...
                </>
              ) : (
                <>
                  🎬 View Solve
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Solve Playback Modal */}
      {showPlayback && playbackData && (
        <SolvePlayback
          playbackData={playbackData}
          onClose={handleClosePlayback}
          theme={theme}
          parentIsDarkMode={parentIsDarkMode}
          level={currentLevel}
          isHardMode={isHardMode}
        />
      )}
    </div>
  );
}
